// Player cell bodies (architecture §9.2 step 6, §9.3; visuals §3, §4; backlog R1-1, R1-2):
// - classic (default): agar's two-fill band. A jelly polygon of N = clamp(round(r_px), 16, 160)
//   vertices (render/jelly.ts offsets) filled at r + off + 5 in the outline colour (each channel
//   × 0.9), then at r + off − 5 in the cell colour: a 10 u band centred on the radius, constant
//   in world units, no stroke. Low / Very low and cells under 8 px draw the same band as two
//   circles. A skin is clipped to the inner shape;
// - sketch: the boiling outline polygon (architecture §9.3) in the cell colour darkened to 72 %,
//   stroked;
// - a skin's manifest `border` colour replaces the outline colour in both styles.
// Labels are drawn by render/labels.ts right after each body.

import type { Rgb } from '@ballio/shared';
import type { AssetImage } from '../assets/loader.js';
import type { Frame } from './frame.js';
import { Wobble } from './noise.js';
import {
  BAND_U,
  boilRadii,
  boilVertexCount,
  fillBand,
  MAX_OUTLINE_VERTICES,
  OUTLINE,
  sketchStrokePx,
  traceCircle,
  traceRing,
} from './outline.js';
import type { SpriteCache } from './sprite-cache.js';
import { css } from './theme.js';

/** Skins are skipped on cells smaller than this on screen (CSS px radius). */
export const MIN_SKIN_RADIUS_PX = 3;
/** The optional cell overlay is drawn only at these levels (art-assets §5: High and above). */
const OVERLAY_LEVELS = new Set(['sharp', 'high']);

/** What one cell looks like this frame (decided by the renderer from the player and settings). */
export interface CellLook {
  fill: Rgb;
  border: Rgb;
  /** The skin image (full size; mips come from the sprite cache), or null. */
  skin: AssetImage | null;
  /** The optional `world/cell-overlay.png` for un-skinned cells, or null. */
  overlay: AssetImage | null;
}

/** A cell as drawn this frame: display position and (eased, effect-applied) radius. */
export interface CellShape {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

export class CellPainter {
  private readonly radii = new Float64Array(MAX_OUTLINE_VERTICES);
  private readonly wobble = new Wobble();

  /**
   * Body, skin (or overlay) and outline of one cell, in the world transform. `off` / `n` are the
   * classic jelly offsets (null for a perfect two-circle band).
   */
  drawBody(
    f: Frame,
    cell: CellShape,
    look: CellLook,
    off: Float64Array | null,
    n: number,
    sprites: SpriteCache,
  ): void {
    if (f.theme.style === 'sketch') {
      this.drawSketch(f, cell, look, sprites);
      return;
    }
    const { ctx } = f;
    const { x, y, r } = cell;
    fillBand(ctx, x, y, r, off, n, css(look.fill), css(look.border), BAND_U);
    // The inner shape is still the current path: clip skins / the overlay to it.
    const image =
      look.skin ?? (look.overlay && OVERLAY_LEVELS.has(f.quality.level) ? look.overlay : null);
    if (!image) return;
    const ri = Math.max(0, r - BAND_U / 2);
    const sprite = look.skin ? sprites.skinLevel(image, 2 * ri * f.scale) : sprites.circle(image);
    if (!sprite) return;
    const clip = off !== null && n >= 3;
    if (clip) {
      ctx.save();
      ctx.clip();
    }
    if (!look.skin) ctx.globalCompositeOperation = 'multiply';
    // The skin bitmap is pre-clipped to a circle; a jelly cell also clips to its polygon.
    const rs = clip ? ri + 2 : ri;
    ctx.drawImage(sprite, x - rs, y - rs, rs * 2, rs * 2);
    if (!look.skin) ctx.globalCompositeOperation = 'source-over';
    if (clip) ctx.restore();
    if (look.skin) f.stats.skinDraws++;
  }

  private drawSketch(f: Frame, cell: CellShape, look: CellLook, sprites: SpriteCache): void {
    const { ctx, zoom } = f;
    const { x, y, r } = cell;
    const rPx = r * zoom;
    const lw = sketchStrokePx(rPx) / zoom;
    let n = 0;
    if (!f.quality.perfectCircles && rPx >= 3) {
      n = Math.min(MAX_OUTLINE_VERTICES, boilVertexCount(rPx));
      this.wobble.seed(cell.id, f.boilFrame);
      boilRadii(this.radii, n, r, this.wobble);
    }
    ctx.beginPath();
    if (n > 0) traceRing(ctx, x, y, this.radii, n, lw / 2);
    else traceCircle(ctx, x, y, Math.max(0, r - lw / 2));
    ctx.fillStyle = css(look.fill);
    ctx.fill();
    const image =
      look.skin ?? (look.overlay && OVERLAY_LEVELS.has(f.quality.level) ? look.overlay : null);
    if (image) {
      const sprite = look.skin ? sprites.skinLevel(image, 2 * r * f.scale) : sprites.circle(image);
      if (sprite) {
        const ri = r * (1 - OUTLINE.boilAmplitude);
        if (!look.skin) ctx.globalCompositeOperation = 'multiply';
        ctx.drawImage(sprite, x - ri, y - ri, ri * 2, ri * 2);
        if (!look.skin) ctx.globalCompositeOperation = 'source-over';
        if (look.skin) f.stats.skinDraws++;
      }
    }
    ctx.lineWidth = lw;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = css(look.border);
    ctx.stroke();
  }
}
